import React, { useEffect } from 'react';
import useAxios from 'axios-hooks';
import { PrimaryButton, ChoiceGroup, MessageBar, MessageBarType, Icon } from '@fluentui/react';
import { Field, Form, Formik } from 'formik';
import { useParams,useHistory } from 'react-router-dom';
import Select from "react-select";
import { FixedSizeList as List } from "react-window";
import TextField from '../../../../Forms/TextField';
import Toast from '../../../../Forms/Toast';
import FeatureSummary from '../../../../Util/FeatureSummary';

const newCoBrandSettingsObject = {
    coBrandId: null,
    domainMask: null,
    isCustomHostname: false,
    subdomain: '',
    baseDomain: 'igrad.com',
    useClientCert: false,
    domainTypeKey: 'subdomain',
    parentWildcard: '',
    childSubdomain: '',
    createWildcard: true
};

const domainTypeOptions = [
    { key: 'subdomain', text: 'iGrad subdomain — acme.igrad.com (we control the DNS)' },
    { key: 'wildcardChild', text: 'Site under a client domain we already host — dobson.wellness.wealthspire.com' },
    { key: 'customHostname', text: 'New client domain — wellness.acme.com (creates a Cloudflare entry)' },
    { key: 'clientCert', text: 'Client domain using their own SSL certificate (bypasses Cloudflare)' }
];

// Asked in business terms; whoever onboards a client should not have to reason about certificate types.
const siteCountOptions = [
    { key: 'multiple', text: 'Multiple sites — hr.wellness.acme.com, ops.wellness.acme.com, …' },
    { key: 'single', text: 'Just this one site — wellness.acme.com' }
];

const DOMAIN_TYPE_KEY_BY_SERVER_VALUE = {
    IGradSubdomain: 'subdomain',
    CloudflareCustomHostname: 'customHostname',
    WildcardChild: 'wildcardChild',
    ClientCertificate: 'clientCert',
    Unresolved: 'unresolved'
};

const DOMAIN_TYPE_LABELS = {
    subdomain: 'iGrad subdomain',
    customHostname: 'Client domain with its own Cloudflare certificate',
    wildcardChild: 'Site under a client domain covered by a wildcard',
    clientCert: 'Client domain with a client-supplied certificate',
    unresolved: 'Client domain not managed by Cloudflare'
};

const baseDomainOptions = [
    { value: 'igrad.com', label: 'igrad.com' },
    { value: 'enrich.org', label: 'enrich.org' }
];

const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        padding: "0 8px",
                        boxSizing: "border-box",
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

const WizardPage = () => {
    const [showToast, setShowToast] = React.useState(false);
    const [toastMessage, setToastMessage] = React.useState("Saved!");
    const [showError, setShowError] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [createdDomain, setCreatedDomain] = React.useState(null);

    const { cobrandedUrlId: idFromParams } = useParams();
    const history = useHistory();

    const [{ data: domain, loading: loading }, getDomain] = useAxios(
        { url: `/internal/cobrand/custom-domain/${idFromParams}`, method: 'GET' },
        { manual: true },
    );
    const [{ data: existingDomains }] = useAxios('/internal/cobrand/custom-domains');

    // Cloudflare is the source of truth for how a domain is served, so nothing here is mirrored locally.
    const [{ data: inventory, loading: loadingInventory, error: inventoryError }] = useAxios(
        '/internal/cobrand/custom-domains/hostname-inventory'
    );

    const [, refreshStatus] = useAxios(
        { method: 'POST' },
        { manual: true },
    );

    useEffect(() => {
        if (idFromParams) {
            getDomain();
        }
    }, [idFromParams]);

    // Fetch live status from Cloudflare when viewing an existing domain
    useEffect(() => {
        if (domain && domain.isCustomHostname && idFromParams) {
            refreshStatus({
                url: `/internal/cobrand/custom-domains/${idFromParams}/refresh-status`
            }).then((result) => {
                if (!result.isAxiosError && result.data) {
                    setCreatedDomain(result.data);
                }
            });
        }
    }, [domain]);

    const [{ data: cobrands, loading: loadingCobrands }] = useAxios('/internal/cobrands');
    const cobrandsOptions = cobrands && cobrands.map(u => ({ value: u.cobrandId, label: u.name }));

    const [{ data, loading: submitting }, submitForm] = useAxios(
        { url: `/internal/cobrand/custom-domains/update` },
        { manual: true },
    );

    // Determine domain type for existing records
    const getDomainType = (domainData) => {
        if (!domainData) return 'subdomain';
        const mapped = DOMAIN_TYPE_KEY_BY_SERVER_VALUE[domainData.domainType];
        if (mapped) return mapped;
        if (domainData.useClientCert) return 'clientCert';
        if (domainData.isCustomHostname) return 'customHostname';
        return 'subdomain';
    };

    return (
        <Formik
            initialValues={domain ? (() => {
                // Fallback: derive subdomain/baseDomain from domainMask if not set on the record
                let subdomain = domain.subdomain || '';
                let baseDomain = domain.baseDomain || '';
                if (!domain.isCustomHostname && domain.domainMask && (!subdomain || !baseDomain)) {
                    const mask = domain.domainMask.toLowerCase();
                    const knownBases = ['igrad.com', 'enrich.org'];
                    const match = knownBases.find(b => mask.endsWith('.' + b));
                    if (match) {
                        baseDomain = baseDomain || match;
                        subdomain = subdomain || mask.slice(0, -('.' + match).length);
                    }
                }
                return {
                    ...domain,
                    subdomain,
                    baseDomain: baseDomain || 'igrad.com',
                    domainTypeKey: getDomainType(domain),
                    parentWildcard: domain.coveringWildcard || '',
                    childSubdomain: domain.coveringWildcard && domain.domainMask
                        ? domain.domainMask.slice(0, -(('.' + domain.coveringWildcard).length))
                        : '',
                    createWildcard: false
                };
            })() : newCoBrandSettingsObject}
            onSubmit={async (values, { resetForm }) => {
                setShowError(false);
                setErrorMessage("");

                const typeKey = values.domainTypeKey;
                const isCustomHostname = typeKey !== 'subdomain';

                // Build the full domain mask
                let fullDomainMask;
                if (typeKey === 'wildcardChild') {
                    fullDomainMask = `${values.childSubdomain?.toLowerCase()}.${values.parentWildcard}`;
                } else if (isCustomHostname) {
                    fullDomainMask = values.domainMask?.toLowerCase();
                } else {
                    fullDomainMask = `${values.subdomain?.toLowerCase()}.${values.baseDomain}`;
                }

                const currentId = idFromParams ? parseInt(idFromParams, 10) : null;

                if (existingDomains && fullDomainMask) {
                    const duplicateMask = existingDomains.some(
                        (d) =>
                            d?.domainMask?.toLowerCase() === fullDomainMask &&
                            d?.cobrandedUrlId !== currentId
                    );
                    const duplicateCobrand = existingDomains.some(
                        (d) =>
                            d?.coBrandId === values.coBrandId &&
                            d?.cobrandedUrlId !== currentId
                    );

                    if (duplicateCobrand) {
                        setShowError(true);
                        setErrorMessage("Selected cobrand already has a domain mask.");
                        return;
                    }

                    if (duplicateMask) {
                        setShowError(true);
                        setErrorMessage("That domain mask is already in use. Please choose another.");
                        return;
                    }
                }

                let result = await submitForm({
                    url: idFromParams ? "/internal/cobrand/custom-domains/update" : "/internal/cobrand/custom-domains/create",
                    method: "POST",
                    data: {
                        CobrandedUrlId: idFromParams,
                        CoBrandId: values.coBrandId,
                        DomainMask: fullDomainMask,
                        IsCustomHostname: isCustomHostname,
                        DomainType: typeKey === 'wildcardChild' ? 'WildcardChild' : null,
                        CreateWildcard: typeKey === 'customHostname' ? values.createWildcard !== false : false,
                        Subdomain: isCustomHostname ? null : values.subdomain?.toLowerCase(),
                        BaseDomain: isCustomHostname ? null : values.baseDomain,
                        UseClientCert: typeKey === 'clientCert'
                    },
                });
                if (!result.isAxiosError) {
                    if(!idFromParams){
                        const resultData = result.data;
                        
                        if (resultData.success === false) {
                            setShowError(true);
                            setErrorMessage(resultData.errorMessage || "Failed to create domain.");
                            return;
                        }
                        
                        const newId = resultData.cobrandedUrlId || resultData;
                        if (newId === 0) {
                            setShowError(true);
                            setErrorMessage("Selected cobrand already has a domain mask.");
                        } else {
                            // Set domain data immediately so DNS config shows while navigating
                            if (resultData.domain) {
                                setCreatedDomain(resultData.domain);
                            }
                            history.push(`/cobrand-settings-feature/masked-domains/${newId}/wizard`);
                            setToastMessage("Saved!");
                            setShowToast(true);
                        }
                    } else {
                        const resultData = result.data;

                        if (resultData && resultData.success === false) {
                            setShowError(true);
                            setErrorMessage(resultData.errorMessage || "Failed to update domain.");
                            return;
                        }

                        getDomain();
                        setToastMessage("Saved!");
                        setShowToast(true);
                    }
                }
            }}
            validate={(values) => {
                const errors = {};
                const typeKey = values.domainTypeKey;

                if (typeKey === 'wildcardChild') {
                    if (!values.parentWildcard) {
                        errors.parentWildcard = "Select the client domain this site sits under";
                    }
                    if (!values.childSubdomain) {
                        errors.childSubdomain = "Subdomain is required";
                    } else if (/\s/.test(values.childSubdomain)) {
                        errors.childSubdomain = "Subdomain cannot contain spaces";
                    } else if (/[^a-zA-Z0-9-]/.test(values.childSubdomain)) {
                        // A wildcard certificate only covers one label, so dots here would not be covered.
                        errors.childSubdomain = "Only one level is allowed, so letters, numbers and hyphens only — no dots";
                    }
                } else if (typeKey === 'customHostname' || typeKey === 'clientCert') {
                    if (!values.domainMask) {
                        errors.domainMask = "Domain is required";
                    } else if (/\s/.test(values.domainMask)) {
                        errors.domainMask = "Domain cannot contain spaces";
                    }
                } else {
                    if (!values.subdomain) {
                        errors.subdomain = "Subdomain is required";
                    } else if (/\s/.test(values.subdomain)) {
                        errors.subdomain = "Subdomain cannot contain spaces";
                    } else if (/[^a-zA-Z0-9.-]/.test(values.subdomain)) {
                        errors.subdomain = "Subdomain can only contain letters, numbers, dots, and hyphens";
                    }
                    if (!values.baseDomain) {
                        errors.baseDomain = "Base domain is required";
                    }
                }
                return errors;
            }}
            validateOnBlur
            enableReinitialize={true}
            isInitialValid={false}
        >
            {({ errors, values, setFieldValue, handleSubmit, dirty }) => {
                const handleRefreshStatus = async () => {
                    if (idFromParams) {
                        const result = await refreshStatus({
                            url: `/internal/cobrand/custom-domains/${idFromParams}/refresh-status`
                        });
                        if (!result.isAxiosError && result.data) {
                            setCreatedDomain(result.data);
                            setToastMessage("Status refreshed!");
                            setShowToast(true);
                        }
                    }
                };

                const isEditing = !!idFromParams;
                const typeKey = values.domainTypeKey || 'subdomain';
                const isSubdomainType = typeKey === 'subdomain';
                const isWildcardChild = typeKey === 'wildcardChild';
                const isNewClientDomain = typeKey === 'customHostname';
                const isClientCert = typeKey === 'clientCert';
                const isUnresolved = typeKey === 'unresolved';

                const activeWildcards = (inventory?.hostnames || []).filter(
                    (h) => h.isWildcard && h.status === 'active'
                );
                const wildcardOptions = activeWildcards.map((w) => ({
                    value: w.hostname,
                    label: w.sslStatus === 'active' ? w.hostname : `${w.hostname} (SSL ${w.sslStatus})`
                }));
                const selectedWildcard = activeWildcards.find((w) => w.hostname === values.parentWildcard);

                const cnameTarget = inventory?.customHostnameCnameTarget || 'customers.igrad.com';
                const clientCertTarget = inventory?.clientCertCnameTarget || 'customers-noproxy.igrad.com';

                // Determine if the form is valid for submission
                const isFormValid = values.coBrandId && Object.keys(errors).length === 0 && (
                    (isSubdomainType && values.subdomain && values.baseDomain) ||
                    (isWildcardChild && values.parentWildcard && values.childSubdomain) ||
                    ((isNewClientDomain || isClientCert) && values.domainMask)
                );

                return <Form style={{ maxWidth: "600px", marginBottom: 500 }}>
                    <h4>Masked Domains</h4>
                    {showError && <div style={{color: 'rgb(164, 38, 44)'}}>{errorMessage}</div>}
                    <div className="mt-1 mb20">
                        <label className='default-label mt20'>Select Cobrand</label>
                        <Select
                            components={{ MenuList: CustomMenuList }}
                            options={cobrandsOptions}
                            placeholder="Select Cobrand"
                            name="coBrandId"
                            value={cobrandsOptions?.find(
                                (option) => option.value === values.coBrandId
                            )}
                            onChange={(option) => {
                                setShowError(false);
                                setErrorMessage("");
                                setFieldValue("coBrandId", option.value);
                            }}
                            isSearchable
                            styles={{
                                menuList: (base) => ({
                                    ...base,
                                    maxHeight: "300px",
                                    overflowY: "auto",
                                }),
                                container: (base) => ({
                                    ...base,
                                    width: 400,
                                }),
                                control: (base) => ({
                                    ...base,
                                    border: "1px solid #aaaaaa",
                                }),
                            }}
                        />
                        
                        {/* Cloudflare decides how every client domain is served, so don't guess while it's unreachable */}
                        {inventoryError && (
                            <MessageBar messageBarType={MessageBarType.error} className="mt20">
                                <strong>Cloudflare is unreachable.</strong> Domain types and statuses can't be determined right now,
                                so creating or editing client domains is disabled. Try again shortly.
                            </MessageBar>
                        )}

                        {/* Domain Type Selection - only for new domains */}
                        {!isEditing && (
                            <div className="mt20">
                                <ChoiceGroup
                                    label="Domain Type"
                                    selectedKey={typeKey}
                                    options={domainTypeOptions}
                                    onChange={(e, option) => {
                                        setShowError(false);
                                        setErrorMessage("");
                                        setFieldValue("domainTypeKey", option.key);
                                        setFieldValue("isCustomHostname", option.key !== 'subdomain');
                                        setFieldValue("useClientCert", option.key === 'clientCert');
                                    }}
                                />
                            </div>
                        )}

                        {/* Show domain type label when editing */}
                        {isEditing && (
                            <div className="mt20" style={{ fontStyle: 'italic', color: '#555' }}>
                                <strong>Type:</strong> {DOMAIN_TYPE_LABELS[typeKey] || 'Unknown'}
                                {domain?.coveringWildcard && (
                                    <span> &mdash; covered by <strong>{domain.coveringWildcard}</strong></span>
                                )}
                            </div>
                        )}

                        {isEditing && isSubdomainType && (
                            <MessageBar messageBarType={MessageBarType.info} className="mt10">
                                You can change the subdomain or reassign this domain to a different cobrand. The matching CNAME record in Cloudflare will be created or updated when you save. The base domain cannot be changed.
                            </MessageBar>
                        )}

                        {isEditing && (isWildcardChild || isClientCert) && (
                            <MessageBar messageBarType={MessageBarType.info} className="mt10">
                                This domain has no Cloudflare entry of its own, so you can rename it or reassign it to another
                                cobrand here. The client must point the new hostname at{' '}
                                <strong>{isClientCert ? clientCertTarget : cnameTarget}</strong> before it will work.
                            </MessageBar>
                        )}

                        {isEditing && isUnresolved && (
                            <MessageBar messageBarType={MessageBarType.info} className="mt10">
                                <div>
                                    Cloudflare holds no certificate for this domain, so it is either served with a certificate the
                                    client supplies or the record is no longer in use. Cloudflare cannot tell those apart.
                                </div>
                                <div className="mt10">
                                    If the client supplies the certificate, their DNS should point at{' '}
                                    <strong>{clientCertTarget}</strong>.
                                </div>
                            </MessageBar>
                        )}

                        {isEditing && isNewClientDomain && (
                            <MessageBar messageBarType={MessageBarType.warning} className="mt10">
                                <div><strong>To change this domain</strong></div>
                                <ol style={{ marginTop: 6, marginBottom: 6, paddingLeft: 20 }}>
                                    <li>Create a new domain mask with the new hostname.</li>
                                    <li>Send the new DNS records to the client.</li>
                                    <li>Wait until the new domain status shows <strong>active</strong> with SSL.</li>
                                    <li>Delete this old record.</li>
                                </ol>
                                <div>This domain has its own certificate, so renaming it in place would break it. The cobrand assignment can still be changed here.</div>
                            </MessageBar>
                        )}

                        {/* Subdomain fields */}
                        {isSubdomainType && (
                            <div className="mt20">
                                <label className='default-label'>Base Domain</label>
                                <Select
                                    options={baseDomainOptions}
                                    placeholder="Select base domain"
                                    name="baseDomain"
                                    value={baseDomainOptions.find(
                                        (option) => option.value === values.baseDomain
                                    )}
                                    onChange={(option) => {
                                        setShowError(false);
                                        setErrorMessage("");
                                        setFieldValue("baseDomain", option.value);
                                    }}
                                    isDisabled={isEditing}
                                    styles={{
                                        container: (base) => ({
                                            ...base,
                                            width: 400,
                                        }),
                                        control: (base) => ({
                                            ...base,
                                            border: "1px solid #aaaaaa",
                                        }),
                                    }}
                                />
                                <TextField
                                    label="Subdomain"
                                    name="subdomain"
                                    className="mt10"
                                    placeholder="e.g., phoenix"
                                    onChange={(e, newValue) => {
                                        setShowError(false);
                                        setErrorMessage("");
                                        setFieldValue("subdomain", newValue);
                                    }}
                                    errorMessage={errors.subdomain}
                                />
                                {values.subdomain && values.baseDomain && (
                                    <div className="mt10" style={{ color: '#0078d4', fontWeight: '600' }}>
                                        Full domain: {values.subdomain.toLowerCase()}.{values.baseDomain}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Site under an existing wildcard - deliberately creates nothing in Cloudflare */}
                        {isWildcardChild && (
                            <div className="mt20">
                                <label className='default-label'>Client Domain</label>
                                <Select
                                    options={wildcardOptions}
                                    placeholder={loadingInventory ? "Loading from Cloudflare…" : "Select the client domain"}
                                    name="parentWildcard"
                                    value={wildcardOptions.find((option) => option.value === values.parentWildcard) || null}
                                    onChange={(option) => {
                                        setShowError(false);
                                        setErrorMessage("");
                                        setFieldValue("parentWildcard", option.value);
                                    }}
                                    isDisabled={isEditing || loadingInventory || !!inventoryError}
                                    styles={{
                                        container: (base) => ({ ...base, width: 400 }),
                                        control: (base) => ({ ...base, border: "1px solid #aaaaaa" }),
                                    }}
                                />
                                {!loadingInventory && !inventoryError && wildcardOptions.length === 0 && (
                                    <MessageBar messageBarType={MessageBarType.warning} className="mt10">
                                        No client domains with a wildcard certificate exist yet. Set the domain up first using
                                        <strong> New client domain</strong>, then come back to add sites under it.
                                    </MessageBar>
                                )}
                                <TextField
                                    label="Subdomain"
                                    name="childSubdomain"
                                    className="mt10"
                                    placeholder="e.g., dobson"
                                    onChange={(e, newValue) => {
                                        setShowError(false);
                                        setErrorMessage("");
                                        setFieldValue("childSubdomain", newValue);
                                    }}
                                    errorMessage={errors.childSubdomain}
                                />
                                {values.childSubdomain && values.parentWildcard && !errors.childSubdomain && (
                                    <div className="mt10" style={{ color: '#0078d4', fontWeight: '600' }}>
                                        Full domain: {values.childSubdomain.toLowerCase()}.{values.parentWildcard}
                                    </div>
                                )}
                                {selectedWildcard && selectedWildcard.sslStatus !== 'active' && (
                                    <MessageBar messageBarType={MessageBarType.severeWarning} className="mt10">
                                        The <strong>{selectedWildcard.hostname}</strong> certificate is currently
                                        <strong> {selectedWildcard.sslStatus}</strong>. Sites added under it will not serve a valid
                                        certificate until that is fixed.
                                    </MessageBar>
                                )}
                                {!errors.childSubdomain && (
                                    <MessageBar messageBarType={MessageBarType.info} className="mt10">
                                        <div><strong>No Cloudflare entry is created</strong> — the existing wildcard certificate already covers this site, so it uses no hostname quota.</div>
                                        <div className="mt10">Send the client one record:</div>
                                        <div style={{ fontFamily: 'monospace', marginTop: 4 }}>
                                            {values.childSubdomain ? `${values.childSubdomain.toLowerCase()}.${values.parentWildcard || '…'}` : '…'} &nbsp;CNAME&nbsp; {cnameTarget}
                                        </div>
                                    </MessageBar>
                                )}
                            </div>
                        )}

                        {/* New client domain, or one using the client's own certificate */}
                        {(isNewClientDomain || isClientCert) && (
                            <div className="mt20">
                                <TextField
                                    label="Domain"
                                    name="domainMask"
                                    className="mt10"
                                    placeholder="e.g., wellness.acme.com"
                                    onChange={(e, newValue) => {
                                        setShowError(false);
                                        setErrorMessage("");
                                        setFieldValue("domainMask", newValue);
                                    }}
                                    errorMessage={errors.domainMask}
                                    disabled={isEditing && !isClientCert}
                                />
                                {!isEditing && isNewClientDomain && (
                                    <div className="mt20">
                                        <ChoiceGroup
                                            label="Will this client have more than one site on this domain?"
                                            selectedKey={values.createWildcard === false ? 'single' : 'multiple'}
                                            options={siteCountOptions}
                                            onChange={(e, option) => {
                                                setFieldValue("createWildcard", option.key === 'multiple');
                                            }}
                                        />
                                        <MessageBar messageBarType={MessageBarType.info} className="mt10">
                                            {values.createWildcard === false ? (
                                                <div>
                                                    The client adds <strong>one CNAME</strong>. Every future site on this domain will
                                                    need its own certificate and will use another Cloudflare hostname slot.
                                                </div>
                                            ) : (
                                                <div>
                                                    The client adds <strong>a CNAME and a TXT record</strong> once. After that, any
                                                    number of sites can be added under this domain with no Cloudflare work and no
                                                    extra quota.
                                                </div>
                                            )}
                                            {inventory && (
                                                <div className="mt10">
                                                    Cloudflare hostnames in use: <strong>{inventory.usedCount} of {inventory.limit}</strong>
                                                </div>
                                            )}
                                        </MessageBar>
                                    </div>
                                )}
                                {isEditing && isClientCert && (
                                    <div className="mt10" style={{ fontStyle: 'italic', color: '#555' }}>
                                        <strong>SSL:</strong> Client-supplied certificate
                                    </div>
                                )}
                                {!isEditing && isClientCert && (
                                    <MessageBar messageBarType={MessageBarType.info} className="mt10">
                                        <div><strong>No Cloudflare entry is created.</strong> The client terminates SSL themselves and is responsible for renewing that certificate.</div>
                                        <div className="mt10">Send the client one record:</div>
                                        <div style={{ fontFamily: 'monospace', marginTop: 4 }}>
                                            {values.domainMask ? values.domainMask.toLowerCase() : '…'} &nbsp;CNAME&nbsp; {clientCertTarget}
                                        </div>
                                    </MessageBar>
                                )}
                            </div>
                        )}

                        <PrimaryButton
                            className="mt20"
                            onClick={() => handleSubmit()}
                            text={isEditing ? "Update" : "Create"}
                            disabled={!isFormValid || submitting || !!inventoryError || (isEditing && !dirty)}
                        />

                        {/* Live status, resolved against Cloudflare */}
                        {createdDomain && createdDomain.isCustomHostname && (
                            <div className="mt30" style={{ 
                                border: '1px solid #0078d4', 
                                borderRadius: '4px', 
                                padding: '20px',
                                backgroundColor: '#f3f9ff',
                                marginTop:10
                            }}>
                                <h4 style={{ marginTop: 0, color: '#0078d4' }}>
                                    <Icon iconName="ServerEnviroment" /> Domain Status
                                </h4>

                                {createdDomain.statusError && (
                                    <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: '12px' } }}>
                                        {createdDomain.statusError}
                                    </MessageBar>
                                )}

                                {createdDomain.domainType === 'ClientCertificate' || createdDomain.domainType === 'Unresolved' ? (
                                    <div style={{ marginBottom: '15px' }}>
                                        {createdDomain.domainType === 'ClientCertificate'
                                            ? 'This domain is served with a certificate the client supplies, so Cloudflare holds no status for it.'
                                            : 'Cloudflare holds no certificate for this domain, so there is no status to report.'}
                                    </div>
                                ) : (
                                    <div style={{ marginBottom: '15px' }}>
                                        {createdDomain.coveringWildcard && (
                                            <div style={{ marginBottom: 8 }}>
                                                Covered by the <strong>{createdDomain.coveringWildcard}</strong> wildcard certificate.
                                            </div>
                                        )}
                                        <strong>Status:</strong>{' '}
                                        <span style={{
                                            color: createdDomain.statusError ? '#a4262c'
                                                : createdDomain.dnsStatus === 'active' ? 'green' : 'orange',
                                            fontWeight: 'bold'
                                        }}>
                                            {createdDomain.statusError ? 'unknown' : (createdDomain.dnsStatus || 'unknown')}
                                        </span>
                                        {(createdDomain.sslStatus || createdDomain.statusError) && (
                                            <span style={{ marginLeft: '15px' }}>
                                                <strong>SSL:</strong>{' '}
                                                <span style={{ 
                                                    color: createdDomain.statusError ? '#a4262c'
                                                        : createdDomain.sslStatus === 'active' ? 'green' : 'orange',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {createdDomain.statusError ? 'unknown' : createdDomain.sslStatus}
                                                </span>
                                            </span>
                                        )}
                                        <PrimaryButton
                                            text="Refresh Status"
                                            onClick={handleRefreshStatus}
                                            style={{ marginLeft: '15px' }}
                                            iconProps={{ iconName: 'Refresh' }}
                                        />
                                    </div>
                                )}

                                {/* DNS Configuration Required section */}
                                {/* Cloudflare reports "initializing" until the certificate order exists; records are absent until then.
                                    HTTP-validated hostnames legitimately have no DNS validation records at all. */}
                                {createdDomain.domainType === 'CloudflareCustomHostname'
                                    && createdDomain.sslStatus === 'initializing' && (
                                    <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginBottom: '12px' } }}>
                                        <strong>The DNS records aren't ready yet.</strong> Cloudflare is still preparing the
                                        certificate. Wait a few seconds and click <strong>Refresh Status</strong> before sending
                                        anything to the client.
                                    </MessageBar>
                                )}

                                {/* Only a domain with its own certificate needs validation records; the other types get
                                    their single CNAME from the form above, and nothing is pending issuance.
                                    A failed lookup means the records on hand cannot be trusted, so they are withheld. */}
                                {createdDomain.domainType === 'CloudflareCustomHostname'
                                    && !createdDomain.statusError
                                    && (createdDomain.cnameTarget || (createdDomain.validationRecords || []).length > 0 || createdDomain.ownershipTxtName) && (
                                    <div style={{
                                        border: '1px solid #d83b01',
                                        borderRadius: '4px',
                                        padding: '16px',
                                        backgroundColor: '#fff4ce',
                                        marginTop: '15px'
                                    }}>
                                        <MessageBar messageBarType={MessageBarType.severeWarning} styles={{ root: { marginBottom: '12px' } }}>
                                            <strong>Important:</strong> Please provide these DNS records to the client. They must add them at their DNS provider for the domain to work. Note the record types — they are not all CNAMEs.
                                        </MessageBar>

                                        {/* CNAME Record 1 - Point domain to iGrad */}
                                        {createdDomain.cnameTarget && (
                                            <div style={{ marginBottom: '16px' }}>
                                                <h5 style={{ margin: '0 0 8px 0', color: '#323130' }}>
                                                    CNAME Record — Point domain to iGrad:
                                                </h5>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                                    <tbody>
                                                        <tr>
                                                            <td style={{ padding: '4px 12px 4px 0', fontWeight: '600', width: '120px' }}>Name / Host</td>
                                                            <td style={{ padding: '4px 0', fontFamily: 'monospace', backgroundColor: '#fff', border: '1px solid #edebe9', padding: '6px 10px', borderRadius: '2px' }}>
                                                                {createdDomain.domain?.domainMask || values.domainMask || '—'}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ padding: '4px 12px 4px 0', fontWeight: '600' }}>Value / Target</td>
                                                            <td style={{ padding: '4px 0', fontFamily: 'monospace', backgroundColor: '#fff', border: '1px solid #edebe9', padding: '6px 10px', borderRadius: '2px' }}>
                                                                {createdDomain.cnameTarget}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ padding: '4px 12px 4px 0', fontWeight: '600' }}>Type</td>
                                                            <td style={{ padding: '4px 0' }}>CNAME</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {/* SSL validation - a wildcard issues two challenges at the same name, both required */}
                                        {(createdDomain.validationRecords || []).length > 0 && (
                                            <div style={{ marginBottom: '16px' }}>
                                                <h5 style={{ margin: '0 0 8px 0', color: '#323130' }}>
                                                    TXT Record{createdDomain.validationRecords.length > 1 ? 's' : ''} — SSL Certificate Validation:
                                                </h5>
                                                {createdDomain.validationRecords.length > 1 && (
                                                    <div style={{ fontSize: '12px', color: '#a4262c', marginBottom: '8px', fontWeight: '600' }}>
                                                        All {createdDomain.validationRecords.length} values must be added at the same name. A wildcard
                                                        certificate needs one challenge for the domain and one for its subdomains.
                                                    </div>
                                                )}
                                                {createdDomain.validationRecords.map((record, index) => (
                                                    <table key={index} style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '10px' }}>
                                                        <tbody>
                                                            <tr>
                                                                <td style={{ padding: '4px 12px 4px 0', fontWeight: '600', width: '120px' }}>Name / Host</td>
                                                                <td style={{ padding: '6px 10px', fontFamily: 'monospace', backgroundColor: '#fff', border: '1px solid #edebe9', borderRadius: '2px' }}>
                                                                    {record.name}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td style={{ padding: '4px 12px 4px 0', fontWeight: '600' }}>Value / Target</td>
                                                                <td style={{ padding: '6px 10px', fontFamily: 'monospace', backgroundColor: '#fff', border: '1px solid #edebe9', borderRadius: '2px' }}>
                                                                    {record.value}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td style={{ padding: '4px 12px 4px 0', fontWeight: '600' }}>Type</td>
                                                                <td style={{ padding: '4px 0' }}>TXT</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                ))}
                                            </div>
                                        )}

                                        {/* TXT Record - Alternative Verification */}
                                        {createdDomain.ownershipTxtName && createdDomain.ownershipTxtValue && (
                                            <div style={{ marginBottom: '16px' }}>
                                                <h5 style={{ margin: '0 0 8px 0', color: '#323130' }}>
                                                    TXT Record — Domain Ownership Verification:
                                                </h5>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                                    <tbody>
                                                        <tr>
                                                            <td style={{ padding: '4px 12px 4px 0', fontWeight: '600', width: '120px' }}>Name / Host</td>
                                                            <td style={{ padding: '4px 0', fontFamily: 'monospace', backgroundColor: '#fff', border: '1px solid #edebe9', padding: '6px 10px', borderRadius: '2px' }}>
                                                                {createdDomain.ownershipTxtName}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ padding: '4px 12px 4px 0', fontWeight: '600' }}>Value / Target</td>
                                                            <td style={{ padding: '4px 0', fontFamily: 'monospace', backgroundColor: '#fff', border: '1px solid #edebe9', padding: '6px 10px', borderRadius: '2px' }}>
                                                                {createdDomain.ownershipTxtValue}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td style={{ padding: '4px 12px 4px 0', fontWeight: '600' }}>Type</td>
                                                            <td style={{ padding: '4px 0' }}>TXT</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        <div style={{ fontSize: '12px', color: '#605e5c', marginTop: '8px', fontStyle: 'italic' }}>
                                            Once the client adds these DNS records, the SSL certificate will be automatically issued and validated. This typically takes a few minutes after the DNS records propagate. Click <strong>Refresh Status</strong> to check the current status.
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <Toast show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
                    <FeatureSummary
                        label="About Custom Domains"
                        summary="Configure the domains cobrand sites are reached on. iGrad subdomains live in a zone we control. Client domains are either given their own Cloudflare certificate, covered by a wildcard already set up for that client, or served with a certificate the client supplies. A wildcard covers exactly one level of subdomain, so sites under it can be added with no Cloudflare work at all."
                    />
                </Form>
            }}
        </Formik>
    );
};

export default WizardPage;
